import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
    Box, Button, CircularProgress, CssBaseline,
    FormControl, Grid, InputAdornment, IconButton,
    OutlinedInput, Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Trans, useTranslation } from 'react-i18next';
import { ProfileSVG, LoginSVG, ShowPasswordSVG, ShowPasswordOffSVG, LockSVG } from '../../components';
import { loginService } from '../../services';
import { createCaptcha, deletCaptcha, showCaptcha, RECAPTCHA_SITE_KEY as SITE_KEY } from '../../helpers';
import LanguageSelector from '../../components/languageSelector';
import CustomAlert from '../../components/Alert';
import platforms from '../../constants/users/platforms';
import FLOW_TYPES from '../../constants/auth/flowTypes';
import errorCodes from '../../constants/pages/errorCode';
import { ROUTES } from '../../constants/routes/routes';
import { palette } from '../../styles/theme';
import '../../styles/sass/auth/login.scss';
import '../../styles/sass/auth/passwordForm.scss';

const RE_LENGTH = /^(?=.{8,})/;
const RE_LOWER = /^(?=.*[a-z])/;
const RE_UPPER = /^(?=.*[A-Z])/;
const RE_NUM = /^(?=.*[0-9])/;
const RE_SPECIAL = /^(?=.*[-+!@#$%^&*])/;

// sessionStorage key that carries the validated OTP + idToken from the OTP
// screen to this one (PASSWORD flow). Must match the key used in Otp.js.
const PASSWORD_RECOVERY_KEY = 'passwordRecovery';
const REDIRECT_DELAY_MS = 3000;
// Required placeholder in the auth payloads
const NO_REPLY_EMAIL = 'no-reply@backoffice.com';
// Backend invalidation code returned when the password contains the user's personal data.
const INVALIDATION_PERSONAL_DATA = 'personal_data';

// Password requirement rows, split into the two display columns. Each item maps
// a `validations` flag to its translated label index (recover.help.list.N).
const PASSWORD_CONDITION_COLUMNS = [
    [
        { flag: 'hasLength', label: 0 },
        { flag: 'hasLower', label: 1 },
        { flag: 'hasUpper', label: 2 }
    ],
    [
        { flag: 'hasNum', label: 3 },
        { flag: 'hasSpecial', label: 4 }
    ]
];

const QUERY_PARAMS = {
    flowType: 'flowType',
    token: 'token',
    idExternal: '_'
};

export default function Password () {
    const { t } = useTranslation();
    const history = useHistory();
    const query = new URLSearchParams(useLocation().search);
    const flow = query.get(QUERY_PARAMS.flowType);

    const isOnboarding = flow === FLOW_TYPES.ONBOARDING;
    const isPasswordRecovery = flow === FLOW_TYPES.PASSWORD;

    // For PASSWORD the OTP + idToken come from sessionStorage (set by Otp.js);
    // the other flows still carry them in the URL.
    const recoveryData = isPasswordRecovery
        ? JSON.parse(sessionStorage.getItem(PASSWORD_RECOVERY_KEY) || '{}')
        : {};
    const token = isPasswordRecovery ? recoveryData.token : query.get(QUERY_PARAMS.token);
    const idToken = isPasswordRecovery ? Number(recoveryData.idToken) : Number(query.get(QUERY_PARAMS.idExternal));

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isVisible, setIsVisible] = useState({ password: false, confirmPassword: false });
    const [validations, setValidations] = useState({});
    const [alert, setAlert] = useState({ severity: '', title: '', description: '', className: '' });
    const [openAlert, setOpenAlert] = useState(false);

    // eslint-disable-next-line no-unused-vars
    const [scriptCaptcha, setScriptCaptcha] = useState('');
    const captchaTokenRef = useRef('');

    const unequalPassword = password && confirmPassword && password !== confirmPassword;

    const onChangePassword = (e) => {
        const pass = e.target.value;
        setPassword(pass);
        const l = RE_LENGTH.test(pass);
        const lo = RE_LOWER.test(pass);
        const u = RE_UPPER.test(pass);
        const n = RE_NUM.test(pass);
        const s = RE_SPECIAL.test(pass);
        setValidations({ hasLength: l, hasLower: lo, hasUpper: u, hasNum: n, hasSpecial: s, isValid: l && lo && u && n && s });
    };

    const validateLink = () => {
        const data = {
            idPlatform: platforms.backoffice,
            email: NO_REPLY_EMAIL,
            metadata: { token },
            idToken
        };
        loginService.validateForgot(data).catch(() => {
            setAlert({
                severity: 'error',
                title: t('errors.generalTitle'),
                description: t('errors.generalDescription'),
                className: 'passwordForm_Alert--error'
            });
            setOpenAlert(true);
        });
    };

    const changePassword = () => {
        setIsLoading(true);

        if (password !== confirmPassword) {
            setAlert({ severity: 'error', title: t('recover.changePassword.error'), className: 'passwordForm_Alert--error' });
            setOpenAlert(true);
            setIsLoading(false);
            return;
        }

        const data = { idPlatform: platforms.backoffice, captchaToken: captchaTokenRef.current, idToken, token, password };

        loginService.change(data)
            .then(() => {
                sessionStorage.removeItem(PASSWORD_RECOVERY_KEY);
                setAlert({ severity: 'success', title: t('recover.recover.success'), className: 'clientsMC_Alert--success' });
                setOpenAlert(true);
                setTimeout(() => { setIsLoading(false); history.push(ROUTES.AUTH.LOGIN); }, REDIRECT_DELAY_MS);
            })
            .catch((e) => {
                const error = e?.response?.data;
                if (error?.code === errorCodes.badRequest && error?.extra?.invalidations?.includes(INVALIDATION_PERSONAL_DATA)) {
                    setAlert({
                        severity: 'error',
                        title: t('errors.generalTitle'),
                        description: t('errors.invalidPassword'),
                        className: 'passwordForm_Alert--error'
                    });
                }
                else {
                    setAlert({
                        severity: 'error',
                        title: t('errors.generalTitle'),
                        description: t('errors.generalDescription'),
                        className: 'passwordForm_Alert--error'
                    });
                }
                setOpenAlert(true);
                setIsLoading(false);
            });
    };

    const onClick = () => {
        setIsLoading(true);
        window.grecaptcha.enterprise.ready(() => {
            window.grecaptcha.enterprise.execute(SITE_KEY, { action: 'RECOVER' })
                .then(tok => { captchaTokenRef.current = tok; changePassword(); })
                .catch(() => {
                    setAlert({
                        severity: 'error',
                        title: t('errors.generalTitle'),
                        description: t('errors.generalDescription'),
                        className: 'passwordForm_Alert--error'
                    });
                    setOpenAlert(true);
                    setIsLoading(false);
                });
        });
    };

    useEffect(() => {
        // PASSWORD: without a token in sessionStorage there is no valid flow
        // (deep link / refresh after it was cleared). Send the user back to login.
        if (isPasswordRecovery && !token) {
            history.replace(ROUTES.AUTH.LOGIN);
            return;
        }
        setValidations({ hasLength: false, hasLower: false, hasUpper: false, hasNum: false, hasSpecial: false, isValid: false });
        // ONBOARDING and PASSWORD: the token was already validated on the OTP screen, do not repeat the call
        if (!isOnboarding && !isPasswordRecovery) {
            const key = `validate:${token}`;
            if (!sessionStorage.getItem(key)) {
                sessionStorage.setItem(key, '1');
                validateLink();
            }
        }
        createCaptcha(setScriptCaptcha, SITE_KEY);
        showCaptcha();
        return () => { deletCaptcha(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Grid container component="main" style={{ minHeight: '100vh' }}>
            <CustomAlert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alert={alert}
                setAlert={setAlert}
            />
            <CssBaseline />

            {/* Left panel */}
            <Grid className="natura-login-left" item xs={6} sm={6} md={6}>
                <LoginSVG />
            </Grid>

            {/* Right panel */}
            <Grid
                className="natura-login-right"
                item
                xs={6}
                sm={6}
                md={6}
                sx={{ position: 'relative', alignItems: 'flex-start !important', overflowY: 'auto' }}
            >
                <LanguageSelector className={{ top: 4, right: 4, position: 'absolute' }} />

                <Box className="login" sx={{ height: 'auto', width: '100%', maxWidth: '35.385rem', boxSizing: 'border-box' }}>
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-start"
                        className="login_card"
                        style={{ height: 'auto' }}
                    >
                        {/* Header */}
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            height="10rem"
                            marginBottom="1.231rem"
                        >
                            <Box className="login_chip">
                                <span className="login_chip--text">{t('login.general.chip')}</span>
                            </Box>
                            <Box className="login_profile--icon">
                                <ProfileSVG />
                            </Box>
                        </Box>

                        {/* Title */}
                        <Typography className="login_card--title">
                            {isOnboarding
                                ? <>{t('recover.titleCreate')}{' '}<span style={{ color: palette.primary.main }}>{t('recover.titleCreate2')}</span></>
                                : <>{t('recover.title')}{' '}<span style={{ color: palette.primary.main }}>{t('recover.title2')}</span></>
                            }
                        </Typography>

                        {/* Subtitle */}
                        <Typography className="login_forgot_password--text" sx={{ mt: 1.5, mb: 2 }}>
                            <Trans i18nKey={'recover.helperText'} />
                        </Typography>

                        {/* Password field */}
                        <FormControl required variant="outlined" fullWidth>
                            <OutlinedInput
                                onChange={onChangePassword}
                                color="secondary"
                                placeholder={isOnboarding ? t('recover.inputsCreate.password.placeholder') : t('recover.inputs.password.placeholder')}
                                type={isVisible.password ? 'text' : 'password'}
                                value={password}
                                sx={{ borderRadius: '0.615rem', height: '3.692rem' }}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <LockSVG color={palette.tertiary[500]} />
                                    </InputAdornment>
                                }
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setIsVisible({ ...isVisible, password: !isVisible.password })}
                                            edge="end"
                                            size="large"
                                        >
                                            {isVisible.password
                                                ? <ShowPasswordSVG fill={palette.secondary[600]} />
                                                : <ShowPasswordOffSVG fill={palette.secondary[600]} />
                                            }
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                        </FormControl>

                        {/* Confirm password field */}
                        <FormControl required variant="outlined" fullWidth sx={{ mt: 1.5, position: 'relative' }}>
                            <OutlinedInput
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                color="secondary"
                                error={!!unequalPassword}
                                placeholder={isOnboarding ? t('recover.inputsCreate.confirmPassword.placeholder') : t('recover.inputs.confirmPassword.placeholder')}
                                type={isVisible.confirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                sx={{ borderRadius: '0.615rem', height: '3.692rem' }}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <LockSVG color={palette.tertiary[500]} />
                                    </InputAdornment>
                                }
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setIsVisible({ ...isVisible, confirmPassword: !isVisible.confirmPassword })}
                                            edge="end"
                                            size="large"
                                        >
                                            {isVisible.confirmPassword
                                                ? <ShowPasswordSVG fill={palette.secondary[600]} />
                                                : <ShowPasswordOffSVG fill={palette.secondary[600]} />
                                            }
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                            {unequalPassword && (
                                <Typography className="password_creator--error">
                                    {t('recover.changePassword.error')}
                                </Typography>
                            )}
                        </FormControl>

                        {/* Conditions */}
                        <Box sx={{ mt: '2rem' }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily: 'Inter, Roboto, sans-serif',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    lineHeight: '1.25rem',
                                    color: palette.primary.main
                                }}
                            >
                                {t('recover.help.title')}
                            </Typography>
                            <Box className="password_conditions--list">
                                {PASSWORD_CONDITION_COLUMNS.map((column, colIndex) => (
                                    <Box key={colIndex} className="password_conditions--col">
                                        {column.map(({ flag, label }) => (
                                            <Box key={flag} className="password_conditions--item">
                                                <CheckCircleIcon fontSize="small" color={validations[flag] ? 'primary' : 'disabled'} />
                                                <Typography className="password_conditions--items">
                                                    {t(`recover.help.list.${label}`)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Submit button */}
                        <Box marginTop="auto" width="100%" sx={{ pt: 2 }}>
                            <Button
                                type="button"
                                fullWidth
                                variant="contained"
                                className="login_submit--button"
                                onClick={onClick}
                                disabled={!validations.isValid || !confirmPassword || !!unequalPassword || isLoading}
                            >
                                {isLoading
                                    ? <CircularProgress size={20} style={{ color: palette.white }} />
                                    : t('buttons.confirm')
                                }
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
}
